import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Clock, CheckCircle2, AlertTriangle, WifiOff, Moon, ChevronLeft as IconChevronLeft, Users, MapPin, Warehouse, Car, Sun,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Button, Field, Input, Select, Textarea,
} from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { queueItem } from "@/lib/offline";

// =============================================================================
// MobileTimesheetEntryScreen — /timesheets/new
// T-278 + T-285 — 4-step wizard, mobile-first.
//
// Step 1 "Where"     : Logging-for picker (manager+) → On site vs In depot
//                      → if On site, project picker.
// Step 2 "When"      : date + quick day chips + start/finish/break + work-hours
//                      total preview (STD/TH/DT split moved to Step 4 summary
//                      because travel hours inflate the total).
// Step 3 "Travel"    : skipped on "In depot" path. Yes/No → if Yes, round-trip
//                      km + travel hours (both optional; can submit blank).
// Step 4 "Wrap up"   : LAFHA (if eligible) + Notes + receipts-nudge +
//                      live summary card (work + travel = total, STD/TH/DT)
//                      + Log hours submit.
//
// Travel hours are summed into total_hours at submit so the existing
// app.compute_hour_split trigger handles the STD/TH/DT split on the combined
// number. Migration 155 added travel_km + travel_hours nullable columns to
// timesheet_entries — see supabase/migrations/155_timesheet_travel_fields.sql.
//
// Offline-queueable via existing IndexedDB. T-008 + T-285.
//
// 22 Oct 2026 (mig 443): a gazetted public holiday shows a badge under the
// date — "didn't work? don't log a depot day, the office puts the paid
// holiday on; worked? every hour is double time" — and the STD/TH/DT preview
// treats the day like a Sunday, as the server split already did.
// =============================================================================

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayOfWeek(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", { weekday: "long" });
}

interface ProjectOption {
  project_id:   string;
  project_code: string;
  project_name: string;
}

interface EmployeeSelf {
  employee_id:    string;
  lafha_eligible: boolean | null;
  lafha_rate:     number | null;
}

interface EmployeePicker {
  employee_id: string;
  first_name:  string | null;
  last_name:   string | null;
  primary_role_name:  string | null;
}

// Default labour budget code — every project has this. Timesheet entries are
// always labour, so we don't prompt for it.
const DEFAULT_LABOUR_CODE = "1010-LAB";

// Placeholder — update via SQL if there's a real overhead code in the chart of accounts.
// All non-project labour rolls here; payroll/manager can recode later if needed.
const OVERHEAD_LABOUR_CODE = "1030-LAB";

type Where = "" | "on_site" | "in_depot";
type TravelChoice = "" | "no" | "yes";
type Step = 1 | 2 | 3 | 4;

// -----------------------------------------------------------------------------

export default function MobileTimesheetEntryScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();

  // ---- Wizard step ---------------------------------------------------------
  const [step, setStep] = useState<Step>(1);

  // ---- Step 1 — Where + project -------------------------------------------
  const [where, setWhere]             = useState<Where>("");
  const [projectId, setProjectId]     = useState("");
  const isInDepot = where === "in_depot";

  // ---- Step 2 — When + work hours -----------------------------------------
  const prefillDate = searchParams.get("date") ?? todayIso();
  const [workDate, setWorkDate]         = useState(prefillDate);
  const [startTime, setStartTime]       = useState("");
  const [endTime, setEndTime]           = useState("");
  const [breakMinutes, setBreakMinutes] = useState<number>(30);
  // T-321 — allowance-only day: away for the project but didn't work hours.
  // Books LAFHA/TA (and optional travel) to the project with total_hours = 0,
  // so crew stop faking a start/finish just to claim the night.
  const [noWork, setNoWork]             = useState(false);
  // T-332 — depot flat day: log a standard day without start/finish/break.
  // Depot-only; "Depot may have work hours more often than flat, but give them
  // the option to tick a flat day" (James). Default 7.6h = one ordinary day.
  const [flatDay, setFlatDay]           = useState(false);
  const [flatHours, setFlatHours]       = useState("7.6");

  // ---- Step 3 — Travel ----------------------------------------------------
  const [travelChoice, setTravelChoice] = useState<TravelChoice>("");
  const [travelKm,     setTravelKm]     = useState<string>("");      // round-trip km, optional
  const [travelHours,  setTravelHours]  = useState<string>("");      // hours behind wheel, optional

  // ---- Step 4 — Extras ----------------------------------------------------
  const [lafhaAway, setLafhaAway] = useState(false);                 // one night per person per day when away
  const [isNight, setIsNight] = useState(false);                     // mig 350 — night works: every hour double time
  const [notes, setNotes]         = useState("");
  const [err, setErr]             = useState<string | null>(null);

  // Manager-on-behalf — manager+ can log hours for any employee. Defaults to
  // self. Field users only ever see their own form.
  const canLogOnBehalf = hasRole(user, "manager");
  const [targetEmployeeId, setTargetEmployeeId] = useState<string>(user?.employee_id ?? "");
  useEffect(() => {
    if (user?.employee_id && !targetEmployeeId) setTargetEmployeeId(user.employee_id);
  }, [user?.employee_id, targetEmployeeId]);
  const isForSelf = !canLogOnBehalf || targetEmployeeId === user?.employee_id;

  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  useEffect(() => {
    const up   = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  // --------------------------------------------------------------------------
  // Queries (employee context + employee picker + project list)
  // --------------------------------------------------------------------------
  const { data: employee } = useQuery({
    queryKey: ["timesheet_employee_self", targetEmployeeId],
    enabled: !!targetEmployeeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, lafha_eligible, lafha_rate")
        .eq("employee_id", targetEmployeeId)
        .single();
      if (error) throw error;
      return data as EmployeeSelf;
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["timesheet_employees_picker"],
    enabled: canLogOnBehalf,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, primary_role_name")
        .eq("is_active", true)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as EmployeePicker[];
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["timesheet_projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, status")
        .in("status", ["live", "mobilising"])
        .order("project_code", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ProjectOption[];
    },
  });

  // mig 443 (22 Oct 2026) — the public holidays in the window the date picker
  // allows (last 30 days → today), so the day gets a badge and nobody logs a
  // depot day on one again. Same table the schedule and Team week read.
  // Cached a day; harmless if offline (the badge just doesn't show).
  const { data: holidays = [] } = useQuery({
    queryKey: ["timesheet_holidays", isoDaysAgo(30), todayIso()],
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("holidays")
        .select("holiday_date, holiday_name, state")
        .gte("holiday_date", isoDaysAgo(30))
        .lte("holiday_date", todayIso())
        .in("state", ["QLD", "NATIONAL"]);
      if (error) throw error;
      return (data ?? []) as { holiday_date: string; holiday_name: string; state: string }[];
    },
  });
  const holidayName = useMemo(() => {
    const h = holidays.find(x => String(x.holiday_date).slice(0, 10) === workDate);
    return h ? h.holiday_name : null;
  }, [holidays, workDate]);

  // --------------------------------------------------------------------------
  // Derived numbers — work hours, travel hours, total, OT split preview
  // --------------------------------------------------------------------------
  const workHours = useMemo<number>(() => {
    if (noWork) return 0;                            // T-321 — allowance-only day
    if (flatDay && isInDepot) {                      // T-332 — flat depot day
      const n = parseFloat(flatHours);
      return isNaN(n) || n < 0 ? 0 : Math.min(n, 16);
    }
    if (!startTime || !endTime) return 0;
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    if ([sh, sm, eh, em].some(n => isNaN(n))) return 0;
    let workMins = (eh * 60 + em) - (sh * 60 + sm);
    if (workMins < 0) workMins += 24 * 60;          // overnight shift
    const totalMins = workMins - (breakMinutes || 0);
    return totalMins > 0 ? totalMins / 60 : 0;
  }, [startTime, endTime, breakMinutes, noWork, flatDay, flatHours, isInDepot]);

  const travelHoursNum = useMemo<number>(() => {
    if (travelChoice !== "yes") return 0;
    const n = parseFloat(travelHours);
    return isNaN(n) || n < 0 ? 0 : n;
  }, [travelChoice, travelHours]);

  const travelKmNum = useMemo<number | null>(() => {
    if (travelChoice !== "yes") return null;
    const n = parseFloat(travelKm);
    return isNaN(n) || n < 0 ? null : n;
  }, [travelChoice, travelKm]);

  // T-285 (revised): total paid hours = workHours (start - finish - break).
  // Travel hours are reporting-only — a sub-breakdown of the worked day, NOT
  // additional time on top. So we DON'T add travel into total. Travel hours
  // should be <= work hours (you can't spend more time driving than you
  // worked). UI surfaces a soft warning if that's violated.
  const totalHours = workHours;
  const travelExceedsWork = travelHoursNum > workHours && workHours > 0;

  // Mirror app.compute_hour_split rules client-side for preview only.
  // Server trigger remains authoritative (public-holiday aware).
  // mig 443 — the preview is holiday-aware too (Sunday / public holiday = all DT).
  const preview = useMemo(() => {
    if (!totalHours || totalHours <= 0) return null;
    const total = totalHours;
    const dow = new Date(workDate).getDay(); // 0=Sun, 6=Sat
    let std = 0, th = 0, dt = 0;
    if (dow === 0 || holidayName) {
      dt = total;
    } else if (dow === 6) {
      th = Math.min(total, 2);
      dt = Math.max(0, total - 2);
    } else {
      std = Math.min(total, 8);
      if (total > 8)  th = Math.min(total - 8, 2);
      if (total > 10) dt = total - 10;
    }
    return { std, th, dt };
  }, [totalHours, workDate, holidayName]);

  // --------------------------------------------------------------------------
  // Step validation + navigation
  // --------------------------------------------------------------------------
  function step1Valid() {
    if (!targetEmployeeId) return false;
    if (where === "") return false;
    if (where === "on_site" && !projectId) return false;
    return true;
  }
  function step2Valid() {
    if (noWork) return true;                         // T-321 — allowance-only: no hours required
    if (flatDay && isInDepot) return workHours > 0 && workHours <= 16;  // T-332 — flat day
    if (!startTime || !endTime) return false;
    if (workHours <= 0) return false;
    if (workHours > 16) return false;
    if (breakMinutes < 30) return false;
    return true;
  }
  function step3Valid() {
    // Travel step is opt-in — the user just needs to have made a Yes/No call.
    // km + hours fields are optional, can submit blank.
    return travelChoice !== "";
  }
  function step4Valid() {
    // Notes required only on the in-depot path (no project context = need a
    // description for the approver).
    if (isInDepot && !flatDay && !notes.trim()) return false;  // flat day skips the note
    return true;
  }

  // Skip step 3 entirely on the In depot path — drove around the yard ≠ site travel.
  function nextStep() {
    setErr(null);
    if (step === 1) {
      if (!step1Valid()) { setErr("Pick where you were and (if on site) the project."); return; }
      setStep(2);
    } else if (step === 2) {
      if (!step2Valid()) { setErr("Enter your start, finish and break — total must be > 0 and ≤ 16h."); return; }
      if (isInDepot) setStep(4); else setStep(3);
    } else if (step === 3) {
      if (!step3Valid()) { setErr("Pick whether you travelled to or from the site today."); return; }
      setStep(4);
    }
  }
  function prevStep() {
    setErr(null);
    if (step === 4) setStep(isInDepot ? 2 : 3);
    else if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
  }

  // --------------------------------------------------------------------------
  // Submit
  // --------------------------------------------------------------------------
  const submitMut = useMutation({
    mutationFn: async () => {
      if (!user?.employee_id) throw new Error("Your login isn't linked to an employee.");
      if (!targetEmployeeId) throw new Error("Pick who you're logging hours for.");
      if (!step1Valid() || !step2Valid() || (!isInDepot && !step3Valid()) || !step4Valid()) {
        throw new Error("Some steps are incomplete — go back and check.");
      }

      const lafhaNights = (employee?.lafha_eligible && lafhaAway) ? 1 : 0;
      const lafhaAmount = lafhaNights > 0 ? (Number(employee?.lafha_rate) || 250) : 0;

      // T-321 — an allowance-only day must actually claim the night away,
      // otherwise there's nothing to record (and we'd just be logging 0h).
      if (noWork && lafhaNights === 0) {
        throw new Error("Allowance-only day: turn on the LAFHA night in the last step, or switch off 'no hours worked'.");
      }

      // 1. Ensure the timesheet period exists (lazy upsert)
      const { data: periodId, error: periodErr } = await supabase
        .schema("app")
        .rpc("ensure_timesheet_period", { p_date: workDate });
      if (periodErr) throw periodErr;

      // When manager logs on behalf, flag the entry so approvers can see who
      // recorded it. Prefix-in-notes keeps the audit trail visible without a
      // new column (created_by_user_id already captures identity for audit_log).
      const onBehalfPrefix = (!isForSelf)
        ? `[logged by manager on ${new Date().toISOString().slice(0, 10)}]`
        : "";
      const isFlat = flatDay && isInDepot;
      const flatPrefix = isFlat ? "[flat day]" : "";
      const finalNotes = [onBehalfPrefix, flatPrefix, notes.trim()].filter(Boolean).join(" ");

      const payload = {
        period_id:     periodId as string,
        employee_id:   targetEmployeeId,
        work_date:     workDate,
        project_id:    isInDepot ? null : projectId,
        budget_code:   isInDepot ? OVERHEAD_LABOUR_CODE : DEFAULT_LABOUR_CODE,
        // T-285: total_hours = work + travel, so the existing server trigger
        // splits STD/TH/DT on the combined number.
        // T-321: allowance-only day books 0 worked hours (no start/finish/break).
        total_hours:   noWork ? 0 : totalHours,
        start_time:    (noWork || isFlat) ? null : startTime,
        end_time:      (noWork || isFlat) ? null : endTime,
        break_minutes: (noWork || isFlat) ? 0 : breakMinutes,
        lafha_nights:  lafhaNights,
        lafha_amount:  lafhaAmount,
        // mig 350 — night works flag: the split trigger books EVERY hour on
        // this entry to double time (James, 4 Sep decision).
        is_night:      noWork ? false : isNight,
        // T-285: reporting columns — null when no travel, else captured values.
        travel_km:     travelKmNum,
        travel_hours:  travelChoice === "yes" ? travelHoursNum : null,
        notes:         finalNotes || null,
        // hours_std / hours_th / hours_dt are computed by the DB trigger —
        // the NOT NULL defaults of 0 let us skip them here.
      };

      if (!online) {
        await queueItem("timesheet_entry", payload);
        return { offline: true };
      }

      const { error } = await (supabase as any).from("timesheet_entries").insert(payload);
      if (error) {
        if ((error as any).code === "23505") {
          throw new Error("You've already logged hours for this project today. Edit that entry from the week view instead.");
        }
        throw error;
      }
      return { offline: false };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["timesheet_entries"] });
      qc.invalidateQueries({ queryKey: ["timesheet_summary"] });
      if (res?.offline) {
        alert("Saved offline. It'll sync when you're back online.");
      }
      navigate("/timesheets");
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Save failed"),
  });

  if (!user) return null;

  // Find the chosen project's display string for the summary
  const chosenProject = projects.find(p => p.project_id === projectId);

  // 4 progress dots — the third dot is greyed when In depot (travel step skipped).
  const stepsForDepot = isInDepot ? [1, 2, 4] : [1, 2, 3, 4];

  return (
    <div className="p-4 md:p-6 max-w-lg mx-auto pb-40">
      <button
        onClick={() => navigate("/timesheets")}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> My timesheet
      </button>

      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-gradient-to-br from-accent/20 to-accent/5 text-accent flex items-center justify-center border border-accent/30">
            <Clock size={22} />
          </div>
          <div className="min-w-0">
            <h1 className="text-[22px] font-semibold text-text">Log hours</h1>
            <p className="text-[13px] text-text-muted mt-0.5">
              {dayOfWeek(workDate)}, {new Date(workDate).toLocaleDateString("en-AU", { day: "numeric", month: "long" })}
            </p>
          </div>
        </div>
        {!online && (
          <div className="mt-3 p-2 rounded bg-warn/10 border border-warn/30 text-[12px] text-warn flex items-center gap-2">
            <WifiOff size={12} /> Offline — entry will queue and sync when you reconnect.
          </div>
        )}
      </div>

      {/* Progress dots */}
      <div className="mb-2 flex items-center gap-1.5">
        {[1, 2, 3, 4].map(n => {
          const isCurrent  = n === step;
          const isPast     = stepsForDepot.indexOf(n) !== -1 && stepsForDepot.indexOf(n) < stepsForDepot.indexOf(step);
          const isSkipped  = n === 3 && isInDepot;
          return (
            <div
              key={n}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                isSkipped
                  ? "bg-ink-raised border border-line-light/30"
                  : isCurrent
                    ? "bg-accent"
                    : isPast
                      ? "bg-accent/40"
                      : "bg-ink-raised"
              }`}
              title={isSkipped ? "Travel step skipped — In depot" : `Step ${n} of 4`}
            />
          );
        })}
      </div>
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-3">
        Step {step}{isInDepot && step === 4 ? " (3 skipped — depot)" : ""} of 4
      </div>

      {/* ============================== STEP 1 — Where ========================= */}
      {step === 1 && (
        <>
          {/* Manager-on-behalf — only for manager+ */}
          {canLogOnBehalf && (
            <Card className="mb-4">
              <div className="p-5">
                <Field
                  label="Logging for"
                  hint={isForSelf
                    ? "This is your timesheet. Switch to log on behalf of a field employee."
                    : "Hours will be recorded against this employee. You'll still be tracked as the entry creator."}
                >
                  <div className="relative">
                    <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                    <Select
                      value={targetEmployeeId}
                      onChange={e => setTargetEmployeeId(e.target.value)}
                      className="pl-9"
                    >
                      {user?.employee_id && (
                        <option value={user.employee_id}>Me (myself)</option>
                      )}
                      {employees
                        .filter(e => e.employee_id !== user?.employee_id)
                        .map(e => {
                          const name = [e.first_name, e.last_name].filter(Boolean).join(" ") || "Unnamed";
                          return (
                            <option key={e.employee_id} value={e.employee_id}>
                              {name}{e.primary_role_name ? ` · ${e.primary_role_name}` : ""}
                            </option>
                          );
                        })}
                    </Select>
                  </div>
                </Field>
                {!isForSelf && (
                  <div className="mt-2 text-[11px] text-warn flex items-center gap-1.5">
                    <AlertTriangle size={11} />
                    Logging on behalf — the entry will be tagged so approvers can see who recorded it.
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Where were you today? */}
          <Card>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-2">
                  Where were you today?
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => { setWhere("on_site"); setFlatDay(false); }}
                    className={`p-4 rounded-md border-2 text-left transition-colors ${
                      where === "on_site"
                        ? "bg-accent/10 border-accent text-text"
                        : "bg-ink-raised border-line hover:border-line-light text-text-muted"
                    }`}
                  >
                    <MapPin size={20} className={where === "on_site" ? "text-accent" : "text-text-muted"} />
                    <div className="mt-2 text-[14px] font-medium text-text">On site</div>
                    <div className="text-[11px] text-text-muted mt-0.5">Working at a project location</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setWhere("in_depot"); setProjectId(""); setNoWork(false); }}
                    className={`p-4 rounded-md border-2 text-left transition-colors ${
                      where === "in_depot"
                        ? "bg-accent/10 border-accent text-text"
                        : "bg-ink-raised border-line hover:border-line-light text-text-muted"
                    }`}
                  >
                    <Warehouse size={20} className={where === "in_depot" ? "text-accent" : "text-text-muted"} />
                    <div className="mt-2 text-[14px] font-medium text-text">In depot</div>
                    <div className="text-[11px] text-text-muted mt-0.5">Yard / vehicle / odd jobs</div>
                  </button>
                </div>
              </div>

              {/* Project picker — only when On site */}
              {where === "on_site" && (
                <Field label="Project *" hint="Pick the project you worked on today.">
                  <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
                    <option value="">Pick a project…</option>
                    {projects.map(p => (
                      <option key={p.project_id} value={p.project_id}>
                        {p.project_code} · {p.project_name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
            </div>
          </Card>
        </>
      )}

      {/* ============================== STEP 2 — When ========================== */}
      {step === 2 && (
        <Card>
          <div className="p-5 space-y-3">
            <Field label="Date">
              <Input type="date" mono value={workDate} onChange={e => setWorkDate(e.target.value)} max={todayIso()} min={isoDaysAgo(30)} />
            </Field>
            <div className="flex gap-2 flex-wrap">
              {[0, 1, 2, 3].map(n => {
                const iso = isoDaysAgo(n);
                const isActive = iso === workDate;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setWorkDate(iso)}
                    className={`px-3 py-1.5 rounded-md text-[12px] border transition-colors ${
                      isActive
                        ? "bg-accent/15 border-accent text-accent"
                        : "bg-ink-raised border-line text-text-muted hover:text-text hover:border-line-light"
                    }`}
                  >
                    {n === 0 ? "Today" : n === 1 ? "Yesterday" : new Date(iso).toLocaleDateString("en-AU", { weekday: "short" })}
                  </button>
                );
              })}
            </div>

            {/* mig 443 — a public holiday is called out before any hours go in.
                The 5 Oct 2026 lesson: a depot day logged on the King's Birthday
                paid double time; the office puts the paid holiday on instead. */}
            {holidayName && (
              <div className="p-3 rounded-md border border-warn/50 bg-warn/10 flex items-start gap-2.5">
                <Sun size={16} className="text-warn flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-[13px] font-medium text-warn">Public holiday — {holidayName}</div>
                  <div className="text-[11px] text-text-muted mt-0.5">
                    Didn't work? <span className="text-text">Don't log a depot day</span> — the office puts your paid public holiday (7.6 h) on for you.
                    Worked it? Log your real hours — every hour on a public holiday pays double time.
                  </div>
                </div>
              </div>
            )}

            {/* T-321 — allowance-only toggle: on-site + LAFHA-eligible only */}
            {!isInDepot && employee?.lafha_eligible && (
              <button
                type="button"
                onClick={() => setNoWork(v => !v)}
                className={`w-full p-3 rounded-md border text-left transition-colors flex items-center justify-between gap-3 ${
                  noWork ? "bg-accent/10 border-accent" : "bg-ink-raised border-line hover:border-line-light"
                }`}
              >
                <div>
                  <div className="text-[13px] font-medium text-text flex items-center gap-1.5">
                    <Moon size={13} className={noWork ? "text-accent" : "text-text-muted"} />
                    No hours worked — allowance only
                  </div>
                  <div className="text-[11px] text-text-muted mt-0.5">
                    Away for the project but didn't work today. Books 0 hours; claim the LAFHA/TA night on the last step.
                  </div>
                </div>
                <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded border flex-shrink-0 ${
                  noWork ? "bg-accent/15 text-accent border-accent/30" : "text-text-muted border-line"
                }`}>
                  {noWork ? "On" : "Off"}
                </span>
              </button>
            )}

            {/* T-332 — depot flat-day toggle */}
            {isInDepot && (
              <button
                type="button"
                onClick={() => { const nv = !flatDay; setFlatDay(nv); if (nv) { setNoWork(false); setLafhaAway(false); } }}
                className={`w-full p-3 rounded-md border text-left transition-colors flex items-center justify-between gap-3 ${
                  flatDay ? "bg-accent/10 border-accent" : "bg-ink-raised border-line hover:border-line-light"
                }`}
              >
                <div>
                  <div className="text-[13px] font-medium text-text flex items-center gap-1.5">
                    <Clock size={13} className={flatDay ? "text-accent" : "text-text-muted"} />
                    Flat day — skip start / finish & breaks
                  </div>
                  <div className="text-[11px] text-text-muted mt-0.5">
                    Logs a standard depot day. Tick for a flat day or flat week; leave off to enter actual times.
                  </div>
                </div>
                <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded border flex-shrink-0 ${
                  flatDay ? "bg-accent/15 text-accent border-accent/30" : "text-text-muted border-line"
                }`}>
                  {flatDay ? "On" : "Off"}
                </span>
              </button>
            )}

            {flatDay && isInDepot && (
              <Field label="Day hours" hint="Standard depot day. Adjust if it ran longer.">
                <Input type="number" inputMode="decimal" mono min={0} max={16} step={0.5}
                  value={flatHours} onChange={e => setFlatHours(e.target.value)} />
              </Field>
            )}

            {!noWork && !(flatDay && isInDepot) && (
              <>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Field label="Start *">
                    <Input type="time" mono value={startTime} onChange={e => setStartTime(e.target.value)} />
                  </Field>
                  <Field label="Finish *">
                    <Input type="time" mono value={endTime} onChange={e => setEndTime(e.target.value)} />
                  </Field>
                </div>
                <Field label="Break (mins)" hint="Fair Work minimum 30 min for shifts of 5+ hours.">
                  <Input
                    type="number"
                    inputMode="numeric"
                    mono
                    min={30}
                    step={5}
                    value={breakMinutes}
                    onChange={e => {
                      const n = parseInt(e.target.value, 10);
                      setBreakMinutes(isNaN(n) ? 30 : Math.max(30, n));
                    }}
                  />
                </Field>
              </>
            )}
            <div className="p-3 rounded bg-accent/5 border border-accent/30">
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1">Day total (paid)</div>
              <div className="text-[22px] font-semibold font-mono text-accent">
                {noWork ? "Allowance only" : (workHours > 0 ? `${workHours.toFixed(2)} hrs` : "—")}
              </div>
              {noWork ? (
                <div className="text-[10px] text-text-muted mt-1">
                  0 paid hours — just the LAFHA/TA night, booked to the project. Set the night on the last step.
                </div>
              ) : !isInDepot && (
                <div className="text-[10px] text-text-muted mt-1">
                  Travel time and km are tracked separately on the next step — they don't change your day total.
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ============================== STEP 3 — Travel ======================== */}
      {step === 3 && (
        <Card>
          <div className="p-5 space-y-4">
            {/* T-285 — make it explicit: tracking only, doesn't add to the day. */}
            <div className="p-3 rounded bg-ink-raised border border-line-light/30 text-[11px] text-text-muted">
              <span className="text-text">Tracking only.</span> Travel km and hours are recorded against this site for fleet/cost visibility. They <span className="text-text">don't change your day total</span> — your pay is based on the start/finish times you entered.
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-2">
                Did you travel to or from this site today?
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setTravelChoice("no")}
                  className={`p-4 rounded-md border-2 text-left transition-colors ${
                    travelChoice === "no"
                      ? "bg-accent/10 border-accent text-text"
                      : "bg-ink-raised border-line hover:border-line-light text-text-muted"
                  }`}
                >
                  <div className="text-[14px] font-medium text-text">No travel</div>
                  <div className="text-[11px] text-text-muted mt-0.5">Lived on-site, stayed nearby, or no driving today</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTravelChoice("yes")}
                  className={`p-4 rounded-md border-2 text-left transition-colors ${
                    travelChoice === "yes"
                      ? "bg-accent/10 border-accent text-text"
                      : "bg-ink-raised border-line hover:border-line-light text-text-muted"
                  }`}
                >
                  <Car size={20} className={travelChoice === "yes" ? "text-accent" : "text-text-muted"} />
                  <div className="mt-2 text-[14px] font-medium text-text">Yes — track km</div>
                  <div className="text-[11px] text-text-muted mt-0.5">Drove to or from the site</div>
                </button>
              </div>
            </div>

            {travelChoice === "yes" && (
              <div className="space-y-3">
                <Field label="Round-trip km" hint="Total km for the day (to + from).">
                  <Input
                    type="number"
                    inputMode="decimal"
                    mono
                    min={0}
                    step="0.1"
                    value={travelKm}
                    placeholder="e.g. 247"
                    onChange={e => setTravelKm(e.target.value)}
                  />
                </Field>
                <Field label="Travel hours" hint="How long behind the wheel.">
                  <Input
                    type="number"
                    inputMode="decimal"
                    mono
                    min={0}
                    step="0.25"
                    value={travelHours}
                    placeholder="e.g. 2.0"
                    onChange={e => setTravelHours(e.target.value)}
                  />
                </Field>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ============================== STEP 4 — Wrap up ======================= */}
      {step === 4 && (
        <>
          {/* Night works — mig 350. Whole entry pays double time. Hidden on an
              allowance-only day (no worked hours to pay). */}
          {!noWork && (
            <Card className="mb-4">
              <div className="p-5">
                <Field label="Night works" hint="Night shift? Every hour on this entry pays double time.">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setIsNight(false)}
                      className={`px-3 py-2.5 rounded-md text-[13px] border transition-colors ${
                        !isNight
                          ? "bg-accent/15 border-accent text-accent"
                          : "bg-ink-raised border-line text-text hover:border-line-light"
                      }`}
                    >
                      Day shift
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsNight(true)}
                      className={`px-3 py-2.5 rounded-md text-[13px] border transition-colors ${
                        isNight
                          ? "bg-accent/15 border-accent text-accent"
                          : "bg-ink-raised border-line text-text hover:border-line-light"
                      }`}
                    >
                      <Moon size={12} className="inline mr-1 align-[-1px]" />
                      Night · all hours 2×
                    </button>
                  </div>
                </Field>
              </div>
            </Card>
          )}

          {/* LAFHA — only if eligible, and not a flat depot day (you're at base,
              not away from home — you wouldn't do both). T-332 */}
          {employee?.lafha_eligible && !(flatDay && isInDepot) && (() => {
            const rate = Number(employee.lafha_rate ?? 250);
            return (
              <Card className="mb-4">
                <div className="p-5">
                  <Field label="Away from home (LAFHA)" hint={`Paid at $${rate.toFixed(0)} for the night if you were staying away.`}>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setLafhaAway(false)}
                        className={`px-3 py-2.5 rounded-md text-[13px] border transition-colors ${
                          !lafhaAway
                            ? "bg-accent/15 border-accent text-accent"
                            : "bg-ink-raised border-line text-text hover:border-line-light"
                        }`}
                      >
                        No
                      </button>
                      <button
                        type="button"
                        onClick={() => setLafhaAway(true)}
                        className={`px-3 py-2.5 rounded-md text-[13px] border transition-colors ${
                          lafhaAway
                            ? "bg-accent/15 border-accent text-accent"
                            : "bg-ink-raised border-line text-text hover:border-line-light"
                        }`}
                      >
                        <Moon size={12} className="inline mr-1 align-[-1px]" />
                        Yes · ${rate.toFixed(0)}
                      </button>
                    </div>
                  </Field>
                </div>
              </Card>
            );
          })()}

          {/* Notes */}
          <Card className="mb-4">
            <div className="p-5">
              <Field
                label={isInDepot && !flatDay ? "Notes *" : "Notes (optional)"}
                hint={isInDepot && !flatDay
                  ? "Required — describe what you did (e.g. 'depot tidy', 'vehicle service', 'training')."
                  : "Anything worth flagging to your supervisor."}
              >
                <Textarea
                  rows={2}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Task, delay, anything worth recording for approval."
                />
              </Field>
            </div>
          </Card>

          {/* Summary card */}
          <Card className="mb-4">
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-2">
                Summary
              </div>
              <div className="space-y-1.5 text-[13px]">
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">When</span>
                  <span className="text-text font-mono">{dayOfWeek(workDate)} {new Date(workDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}{noWork ? " · no hours" : (flatDay && isInDepot) ? ` · ${workHours.toFixed(1)}h flat` : ` · ${startTime || "—"}–${endTime || "—"}`}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">Where</span>
                  <span className="text-text">
                    {isInDepot
                      ? "In depot"
                      : (chosenProject
                          ? `${chosenProject.project_code} · ${chosenProject.project_name}`
                          : "—")}
                  </span>
                </div>
                {/* T-285: total day = workHours from start/finish/break. Travel
                    is a breakdown line under it, NOT additive. */}
                <div className="flex justify-between gap-3 border-t border-line pt-1.5 mt-1.5">
                  <span className="text-text-muted">Day total (paid)</span>
                  <span className="text-accent font-semibold font-mono text-[15px]">{noWork ? "Allowance only" : `${totalHours.toFixed(2)}h`}</span>
                </div>
                {travelChoice === "yes" && (travelHoursNum > 0 || travelKmNum !== null) && (
                  <div className="flex justify-between gap-3 pl-3 border-l-2 border-line-light/40">
                    <span className="text-text-muted text-[11px]">↳ of which travel <span className="text-text-dim">(tracking only)</span></span>
                    <span className="text-text-muted font-mono text-[11px]">
                      {travelHoursNum > 0 ? `${travelHoursNum.toFixed(2)}h` : "—"}
                      {travelKmNum !== null ? ` · ${travelKmNum.toFixed(0)}km` : ""}
                    </span>
                  </div>
                )}
                {travelExceedsWork && (
                  <div className="flex items-center gap-1.5 text-[11px] text-warn mt-1">
                    <AlertTriangle size={11} />
                    Travel hours ({travelHoursNum.toFixed(2)}h) exceed your day total ({workHours.toFixed(2)}h) — double-check the numbers.
                  </div>
                )}
              </div>

              {preview && (
                <div className="mt-3 p-3 rounded bg-ink-raised border border-line">
                  <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1.5">
                    Hours split (preview)
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[12px] font-mono">
                    <div><span className="text-text-muted">STD</span> <span className="text-text">{preview.std.toFixed(1)}</span></div>
                    <div><span className="text-text-muted">1.5x</span> <span className="text-warn">{preview.th.toFixed(1)}</span></div>
                    <div><span className="text-text-muted">2x</span> <span className="text-danger">{preview.dt.toFixed(1)}</span></div>
                  </div>
                  <div className="text-[10px] text-text-muted mt-1.5">
                    Server recalculates on save using public-holiday awareness.
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* T-374 — reimbursement nudge REMOVED (James: no navigation jumping;
              the guys snap receipts in the normal receipt-capture flow. Their
              paid-back status shows on the personal timesheets screen.) */}
        </>
      )}

      {/* ============================== Sticky bottom bar ====================== */}
      <StickyBottomBar>
        {err && (
          <div className="px-3 py-2 bg-red-900/30 border-b border-red-900/50 text-red-200 text-[12px] text-center flex items-center justify-center gap-1.5">
            <AlertTriangle size={12} />
            {err}
          </div>
        )}
        {step === 4 && isInDepot && !flatDay && !notes.trim() && (
          <div className="px-3 pt-2 max-w-lg mx-auto">
            <p className="text-[12px] text-warn text-center">
              Add a note above (what you did in the depot) to enable “Log hours”.
            </p>
          </div>
        )}
        <div className="p-3 flex gap-2 max-w-lg mx-auto">
          {step === 1 ? (
            <Button variant="secondary" size="lg" onClick={() => navigate("/timesheets")} type="button">
              Cancel
            </Button>
          ) : (
            <Button variant="secondary" size="lg" icon={IconChevronLeft} onClick={prevStep} type="button">
              Back
            </Button>
          )}

          {step < 4 ? (
            <Button
              size="lg"
              onClick={nextStep}
              className="flex-1 justify-center"
              type="button"
            >
              Next
            </Button>
          ) : (
            <Button
              size="lg"
              icon={CheckCircle2}
              onClick={() => submitMut.mutate()}
              disabled={submitMut.isPending || !step4Valid()}
              className="flex-1 justify-center"
              type="button"
            >
              {submitMut.isPending ? "Saving…" : online ? "Log hours" : "Queue offline"}
            </Button>
          )}
        </div>
      </StickyBottomBar>
    </div>
  );
}
